import React, { useEffect, useState } from 'react'
import { selectPeaksObject, selectAreaUnderPeaks } from '../../lib/utilities/selector';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import CalibrationCurveChart from '../shared/CalibrationCurveChart';
import { MdOutlineChevronRight } from "react-icons/md";
import { savePeaksObject } from '../../reduxSlices/results/resultsSlice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom'
import ResizableDiv from '../shared/ResizableDiv';
import BackButton from '../shared/BackButton';
import ConfirmationModal from '../shared/ConfirmationModal';
import { useConfigNavigationGuard } from '../../hooks/useConfigNavigationGuard';
import { resetAll } from '../../reduxSlices/results/resultsSlice';






function CalibrationCurve() {

    const peaksObject = useSelector(selectPeaksObject);
    const areaUnderPeaksArray = useSelector(selectAreaUnderPeaks);
    // const visiblePeaks = peaksObject.filter((peak) => peak.show);
    const [selectedPeak, setSelectedPeak] = useState(null);
    const [data, setData] = useState(null);
    const [chartBullets, setChartBullets] = useState(null);
    const [errorsByPeak, setErrorsByPeak] = useState({});
    const [concentrationByPeak, setConcentrationByPeak] = useState({});

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Navigation guard
    const { showExitModal, handleConfirmExit, handleCancelExit } = useConfigNavigationGuard(
        ['/configuration/peaksOnQc', '/configuration/peaksApproval'],
        () => dispatch(resetAll())
    );

    useEffect(() => {
        if (peaksObject.length > 0) {

            // Compute errors for all peaks on mount
            peaksObject.forEach((peak, index) => {
                if (peak.show) {
                    chartDataCreator(peak, index);
                }
            });

            const firstVisiblePeakIndex = peaksObject.findIndex((peak) => peak.show);
            if (firstVisiblePeakIndex !== -1) {
                const firstVisiblePeak = peaksObject[firstVisiblePeakIndex];
                setSelectedPeak(firstVisiblePeak.peak);
                chartDataCreator(firstVisiblePeak, firstVisiblePeakIndex);
            }
        }
    }, [peaksObject]);



    const handleButtonClick = (peak, index) => {
        setSelectedPeak(peak.peak);
        chartDataCreator(peak, index);

    };

    const chartDataCreator = (peak, index) => {
        const coefficients = peak.equation.coefficients;

        // Dynamically get the first and last key-value pairs from the tc array
        const firstItem = peak.tc[0]; // First object
        const lastItem = peak.tc[peak.tc.length - 1]; // Last object

        // Extract the key and value dynamically for the first and last items
        const firstKey = Object.keys(firstItem).find(key => key !== "userEdited");
        const lastKey = Object.keys(lastItem).find(key => key !== "userEdited");

        const firstTc = firstItem[firstKey] - 10 % firstItem[firstKey];
        const lastTc = lastItem[lastKey] + 10 % lastItem[lastKey];

        // Calculate y values using the equation
        const b = (firstTc - coefficients[1]) / coefficients[0];
        const d = (lastTc - coefficients[1]) / coefficients[0];

        // Create the array of objects
        const result = [
            { x: firstTc, y: b },
            { x: lastTc, y: d },
        ];
        setData(result);
        // console.log(result)

        const measuredConcentrations = computeMeasuredConcentrations(areaUnderPeaksArray[index], coefficients);
        // console.log(measuredConcentrations); 
        setConcentrationByPeak((prev) => ({
            ...prev,
            [peak.peak]: measuredConcentrations, // Use peak.peak as the key
        }));

        const tcObject = peak.tc.reduce((acc, obj) => {
            // Get the key and value from the current object
            const [key, value] = Object.entries(obj).find(([key]) => key !== "userEdited");
            acc[key] = value; // Add it to the accumulator
            return acc;
        }, {});

        const errors = calculateConcentrationErrors(tcObject, measuredConcentrations);
        // console.log(errors);
        // Update errorsByPeak with the new errors
        setErrorsByPeak((prev) => ({
            ...prev,
            [peak.peak]: errors, // Use peak.peak as the key
        }));

        const concentrationPointsArray = Object.keys(measuredConcentrations).map(key => ({
            x: measuredConcentrations[key],
            y: areaUnderPeaksArray[index][key],
            error: errors[key] || null,
        }));
        setChartBullets(concentrationPointsArray);

    }

    const calculateConcentrationErrors = (targetConcentrations, measuredConcentrations) => {
        /**
         * Calculate the percentage error for each standard's measured concentration
         * relative to its target concentration.
         *
         * @param {Object} targetConcentrations - A dictionary of target concentrations, e.g. {"std1": 1.0, "std2": 5.0, ...}
         * @param {Object} measuredConcentrations - A dictionary of measured concentrations, e.g. {"std1": 0.9, "std2": 4.8, ...}
         * @return {Object} - A dictionary of percentage errors, keyed by the same keys as the input dictionaries
         */
        const errors = {};
        for (const [stdName, targetVal] of Object.entries(targetConcentrations)) {
            const measuredVal = measuredConcentrations[stdName];
            if (measuredVal === undefined) {
                // Handle missing values, e.g., assign null or any other appropriate value
                errors[stdName] = null;
            } else {
                // Calculate percentage error
                const errorPercentage = (Math.abs(measuredVal - targetVal) / targetVal) * 100;
                errors[stdName] = Math.round(errorPercentage * 100) / 100; // Round to two decimals
            }
        }
        return errors;
    }

    const computeMeasuredConcentrations = (peakAreas, coefficients) => {
        /**
         * Computes measured concentrations using coefficients of a polynomial equation.
         *
         * @param {Object} peakAreas - An object containing peak areas, e.g., { sample1: 1.2, sample2: 2.5, ... }.
         * @param {Array} coefficients - An array of coefficients for the polynomial equation, e.g., [a, b, c] for ax^2 + bx + c.
         * @return {Object} - A dictionary of measured concentrations keyed by the same keys as in peakAreas.
         */
        const measuredConcentrations = {};

        // Function to compute polynomial value given x
        const evaluatePolynomial = (x, coeffs) => {
            return coeffs.reduce((acc, coeff, index) => acc + coeff * Math.pow(x, coeffs.length - 1 - index), 0);
        };

        // Compute concentrations for each sample
        for (const [sampleName, area] of Object.entries(peakAreas)) {
            const concentration = evaluatePolynomial(area, coefficients);
            measuredConcentrations[sampleName] = concentration;
        }

        return measuredConcentrations;
    }

    const handleNextScreen = () => {
        const updatedPeaksObject = peaksObject.map((peakObj) => ({
            ...peakObj,
            errors: errorsByPeak[peakObj.peak] || null, // Attach errors or null if not present
            stdConcentrations: concentrationByPeak[peakObj.peak] || null, // Attach concentrations or null if not present
        }));

        // Only save to Redux if the computed data has changed
        if (!isEqual(updatedPeaksObject, peaksObject)) {
            dispatch(savePeaksObject(updatedPeaksObject));
        }
        navigate('/configuration/peaksOnQc');
    }



    return (
        <div className=' flex flex-col overflow-hidden'>
            <ConfirmationModal
                isOpen={showExitModal}
                onClose={handleCancelExit}
                onConfirm={handleConfirmExit}
                title="Leave Configuration?"
                message="If you leave now, the current configuration will not be saved and all progress will be lost. Are you sure you want to continue?"
                confirmText="Leave"
                cancelText="Stay"
                type="warning"
            />
            <ResizableDiv initialHeight={400} minHeight={400} className='rounded-sm shadow-md px-5 py-2 my-8 mx-16'>
                {!data ?
                    <div className='text-center '>Click on 'Show Calibration Curve' button for each peak to see the chart</div>
                    :
                    <CalibrationCurveChart data={data} chartBullets={chartBullets} />
                }
            </ResizableDiv>
            <div className='mt-5 p-5 flex-grow flex justify-center items-center overflow-auto mb-24'>
                <table className="table-auto border-collapse border border-gray-300 bg-gray-200">
                    <thead>
                        <tr>
                            {peaksObject.map((peak) => {
                                if (peak.show) {
                                    return (
                                        <th
                                            key={peak.peak}
                                            className={`border border-gray-300  text-center text-gray-200 bg-customeBlack ${selectedPeak === peak.peak ? '' : ''
                                                }`}
                                        >
                                            {peak.name}
                                        </th>

                                    );
                                }
                                return null;
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {peaksObject.map((peak) => {
                                if (peak.show) {
                                    return (
                                        <td
                                            key={peak.peak}
                                            className={`border border-gray-300 px-4 py-2 ${selectedPeak === peak.peak ? 'bg-gray-300' : ''
                                                }`}
                                        >
                                            {errorsByPeak[peak.peak] ? (
                                                <>
                                                    <div>Errors:</div>
                                                    {Object.entries(errorsByPeak[peak.peak]).map(([std, error]) => {
                                                        // Get the target RSD for this standard
                                                        const targetRSD = peak.stdTargetRSD?.find(rsdObj => std in rsdObj)?.[std];
                                                        const isOverLimit = targetRSD && error > parseFloat(targetRSD);

                                                        return (
                                                            <div key={std} className='m-2'>
                                                                {std.split('.').slice(0, -1).join('.')}: <strong className={isOverLimit ? 'text-red-500' : 'text-gray-900'}>{error}%</strong>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            ) : (
                                                'No errors calculated'
                                            )}
                                        </td>
                                    );
                                }
                                return null;
                            })}
                        </tr>
                        <tr>
                            {peaksObject.map((peak, index) => {
                                if (peak.show) {
                                    return (
                                        <td
                                            key={peak.peak}
                                            className={`border border-gray-300 px-4 py-2 text-center ${selectedPeak === peak.peak ? 'bg-gray-300' : ''
                                                }`}
                                        >
                                            <button
                                                className={`mt-2 px-4 py-2 text-white hover:bg-customeOrange rounded ${selectedPeak === peak.peak
                                                        ? 'bg-customeOrange'
                                                        : 'bg-gray-400'
                                                    }`}
                                                onClick={() => handleButtonClick(peak, index)}
                                            >
                                                {selectedPeak === peak.peak ? 'Show Calibration Curve' : 'Show Calibration Curve'}
                                            </button>
                                        </td>
                                    );
                                }
                                return null;
                            })}
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className='fixed bottom-8 right-8 z-40 flex gap-2'>
                <BackButton to={'/configuration/peaksApproval'} />

                <button
                    // disabled={!allTcValuesFilled}
                    onClick={() => handleNextScreen()}
                    className={`flex-row flex items-center justify-center bg-buttonColorActive text-white font-semibold w-32 p-3 rounded-md self-center disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed hover:bg-customeOrange hover:text-customeBlack`}
                >Next
                    <MdOutlineChevronRight className='ml-6' fontSize={24} />
                </button>
            </div>
        </div>
    )
}

export default CalibrationCurve


// peaksObject: [
//     {
//         'peak': 1,
//         'show': true,
//         'name': 'Peak 1',
//         equation: {'coefficients': [1.342,  5.645]},
//         'tc': [
//             {
//                 'a': 1,
//                 'b': 2,
//                 ...
//             }
//         ]
//     },
//     {
//         'peak': 2,
//         'show': false,
//         'name': 'Peak 2',
//         equation: {'coefficients': [2.3243,  3.5666]},
//         'tc': [
//             {
//                 'a': 1,
//                 'b': 2,
//                 ...
//             }
//         ]
//     },
//     ...
// ]